/**
 * Decomposes a concave building footprint into rectangular-ish "lobes" so each
 * wing gets its own roof, instead of raising one oriented-bbox canopy over the
 * whole ring (issue #114). The whole-ring canopy spans courtyards and gaps
 * between wings — on the corpus's larger multi-wing footprints that reads as a
 * "massive shopping-mall" roof floating 10–16 m above the eaves. Roofing each
 * lobe independently keeps every ridge close to real walls.
 *
 * Pure, three.js-free, deterministic, and unit-tested directly in Node —
 * mirrors the geometry-builder pattern in buildings.ts (footprintToLocal,
 * ridgeAxis, buildRoof). Nothing here touches raypath/ or LOS: building
 * massing is scene furniture only (hard constraint #1).
 *
 * Algorithm (recursive reflex-cut splitting in the ring's own local frame):
 *   1. If the piece's oriented-bbox roof already sits within ROOF_LOBE_MARGIN_M
 *      of the footprint polygon, accept it as one lobe. A near-rectangular
 *      footprint (all four #104 golden fixtures, and the vast majority of
 *      cottages) has ~0 overhang, so it stays a single lobe and buildRoof
 *      reproduces today's output byte-for-byte.
 *   2. Otherwise cut at a reflex vertex by extending one of its incident edges
 *      into the interior until it meets the boundary — the cut lies along a
 *      real wall direction, so L/T/U/plus/staircase footprints split into
 *      wall-aligned rectangles. Among all such candidate cuts, take the one
 *      whose two children have the smallest worst-case overhang (deterministic
 *      tie-breaks), and recurse on both children.
 *   3. A convex piece that still overhangs (a triangle/trapezoid/skew quad with
 *      no reflex vertex to cut) is halved by a guillotine cut perpendicular to
 *      its long axis; halving shrinks the overhang geometrically, so recursion
 *      converges.
 *
 * The accept test drives decomposition off the *actual* overhang, not the
 * issue's area-ratio proxy: 94 real footprints have area-ratio >= 0.85 yet
 * their single-bbox roof overhangs the polygon by >2 m (dense-but-concave
 * arrow/L/fan shapes), so the 2 m acceptance bound and a pure area-ratio gate
 * cannot both hold. The bound wins (it is the issue's entire purpose and a
 * full-corpus acceptance criterion); orientedBboxAreaRatio is exported for the
 * near-rectangular identity regression but is not itself the gate.
 */
import type { LocalXZ } from '../core/coordinates';

/**
 * Accept a piece as a single lobe once no corner of its oriented bounding box
 * lies more than this far (metres) outside the footprint polygon. Set below the
 * issue's 2 m acceptance bound so the built roof clears it with headroom — the
 * built roof's outermost vertices are exactly these bbox corners, so an
 * accepted lobe is provably within this distance by the same primitive the
 * acceptance sweep uses.
 */
export const ROOF_LOBE_MARGIN_M = 1.7;

/**
 * A lobe whose short dimension (full span) is under this is a needle: buildRoof
 * caps it flat instead of raising a pitched apex, so decomposition never emits
 * spindly roof slivers (issue #114 predictable-failure-cases table).
 */
export const SLIVER_SPAN_M = 1.5;

/** Recursion safety rails — deep enough that the whole corpus converges well
 * within them (no real footprint reaches either), but bounded so a pathological
 * ring can never loop or explode the geometry. */
const MAX_LOBE_DEPTH = 20;
const MAX_LOBES = 64;

const EPS = 1e-7;

/** Oriented (minimum-along-longest-edge) bounding box of a footprint ring —
 * the same box ridgeAxis (buildings.ts) raises a roof over, plus its
 * cross-ridge unit vector. Kept in sync with ridgeAxis: identical arithmetic,
 * so a lobe accepted here roofs to exactly the extent tested here. */
export interface OrientedBox {
  /** unit direction of the longest edge (the ridge/along axis) */
  dir: { x: number; z: number };
  /** cross-ridge unit vector (dir rotated +90 degrees) */
  perp: { x: number; z: number };
  /** half-extent along the ridge axis, metres */
  halfLength: number;
  /** half-extent across the ridge axis, metres */
  halfSpan: number;
  /** box centre, local x/z */
  centre: LocalXZ;
}

/** Signed shoelace area (positive or negative by winding); callers take abs where they want unsigned area. */
function signedArea(ring: LocalXZ[]): number {
  let a = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    a += ring[j].x * ring[i].z - ring[i].x * ring[j].z;
  }
  return a / 2;
}

/** Absolute polygon area (m^2). */
export function polygonArea(ring: LocalXZ[]): number {
  return Math.abs(signedArea(ring));
}

/**
 * Oriented bounding box of the closed ring, longest edge as the along axis.
 * Mirrors ridgeAxis in buildings.ts vertex-for-vertex — the two must agree so
 * that a lobe accepted by its overhang here is roofed to that same extent.
 */
export function orientedBbox(ring: LocalXZ[]): OrientedBox {
  const n = ring.length;
  let dir = { x: 1, z: 0 };
  let maxLenSq = -Infinity;
  for (let i = 0; i < n; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % n];
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const lenSq = dx * dx + dz * dz;
    if (lenSq > maxLenSq) {
      maxLenSq = lenSq;
      const len = Math.sqrt(lenSq);
      dir = len > 1e-9 ? { x: dx / len, z: dz / len } : { x: 1, z: 0 };
    }
  }
  const perp = { x: -dir.z, z: dir.x };

  let minAlong = Infinity;
  let maxAlong = -Infinity;
  let minAcross = Infinity;
  let maxAcross = -Infinity;
  for (const p of ring) {
    const along = p.x * dir.x + p.z * dir.z;
    const across = p.x * perp.x + p.z * perp.z;
    if (along < minAlong) minAlong = along;
    if (along > maxAlong) maxAlong = along;
    if (across < minAcross) minAcross = across;
    if (across > maxAcross) maxAcross = across;
  }
  const alongCentre = (minAlong + maxAlong) / 2;
  const acrossCentre = (minAcross + maxAcross) / 2;
  return {
    dir,
    perp,
    halfLength: (maxAlong - minAlong) / 2,
    halfSpan: (maxAcross - minAcross) / 2,
    centre: {
      x: alongCentre * dir.x + acrossCentre * perp.x,
      z: alongCentre * dir.z + acrossCentre * perp.z,
    },
  };
}

/** The four corners of an oriented box, local x/z — the eave corners of any roof over it. */
export function orientedBboxCorners(box: OrientedBox): LocalXZ[] {
  const corners: LocalXZ[] = [];
  for (const along of [-1, 1]) {
    for (const across of [-1, 1]) {
      corners.push({
        x: box.centre.x + box.dir.x * along * box.halfLength + box.perp.x * across * box.halfSpan,
        z: box.centre.z + box.dir.z * along * box.halfLength + box.perp.z * across * box.halfSpan,
      });
    }
  }
  return corners;
}

/**
 * Every horizontal position a gable/hip/flat roof over `lobe` places a vertex:
 * the four eave corners, plus the ridge points at the lobe's cross-centre for
 * each roof type (gable ends at +/-halfLength, hip ends at +/-(halfLength -
 * halfSpan), and the collapsed-pyramid apex at the centre). The ridge points
 * matter as much as the corners — a concave lobe roofed as one gable runs its
 * ridge across the lobe's own notch, so the apex, not an eave corner, is the
 * vertex that strays outside the footprint (issue #114 defect). Bounding these
 * positions is what keeps the built roof — not merely its eaves — within the
 * acceptance margin.
 */
export function lobeRoofVertexPositions(lobe: LocalXZ[]): LocalXZ[] {
  const box = orientedBbox(lobe);
  const positions = orientedBboxCorners(box);
  const hipInset = Math.max(0, box.halfLength - box.halfSpan);
  for (const along of [box.halfLength, -box.halfLength, hipInset, -hipInset, 0]) {
    positions.push({ x: box.centre.x + box.dir.x * along, z: box.centre.z + box.dir.z * along });
  }
  return positions;
}

/**
 * True when a lobe is a needle — either dimension of its oriented box under
 * SLIVER_SPAN_M. buildRoof caps such a lobe flat rather than raising a spindly
 * pitched apex over it (issue #114 predictable-failure-cases table). No real
 * whole footprint is a needle (min corpus span ~1.94 m), so this only ever
 * fires on thin sub-lobes produced by decomposition.
 */
export function isSliverLobe(lobe: LocalXZ[]): boolean {
  const box = orientedBbox(lobe);
  return 2 * box.halfSpan < SLIVER_SPAN_M || 2 * box.halfLength < SLIVER_SPAN_M;
}

/** Footprint area as a fraction of its oriented-bbox area — 1 for a rectangle, lower the more concave the ring. */
export function orientedBboxAreaRatio(ring: LocalXZ[]): number {
  const box = orientedBbox(ring);
  const bboxArea = box.halfLength * 2 * (box.halfSpan * 2);
  return bboxArea > 1e-9 ? polygonArea(ring) / bboxArea : 1;
}

/** Even-odd (ray-casting) point-in-polygon test. */
export function pointInPolygon(p: LocalXZ, ring: LocalXZ[]): boolean {
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = ring[i].x;
    const zi = ring[i].z;
    const xj = ring[j].x;
    const zj = ring[j].z;
    const intersect = zi > p.z !== zj > p.z && p.x < ((xj - xi) * (p.z - zi)) / (zj - zi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function distanceToSegment(p: LocalXZ, a: LocalXZ, b: LocalXZ): number {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const lenSq = dx * dx + dz * dz;
  let t = lenSq > 0 ? ((p.x - a.x) * dx + (p.z - a.z) * dz) / lenSq : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.z - (a.z + t * dz));
}

/** Horizontal distance from a point to a footprint polygon: 0 if inside, else the nearest-edge distance. */
export function distanceToPolygon(p: LocalXZ, ring: LocalXZ[]): number {
  if (pointInPolygon(p, ring)) return 0;
  let min = Infinity;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    min = Math.min(min, distanceToSegment(p, ring[j], ring[i]));
  }
  return min;
}

/**
 * Worst-case horizontal distance any roof vertex over `lobe` (its eave corners
 * and every roof-type ridge point) would lie outside `original`. This is the
 * quantity decomposition drives below the acceptance margin, and the same
 * quantity the acceptance sweep measures on the built roof.
 */
export function lobeOverhang(lobe: LocalXZ[], original: LocalXZ[]): number {
  let max = 0;
  for (const p of lobeRoofVertexPositions(lobe)) max = Math.max(max, distanceToPolygon(p, original));
  return max;
}

function cross(o: LocalXZ, a: LocalXZ, b: LocalXZ): number {
  return (a.x - o.x) * (b.z - o.z) - (a.z - o.z) * (b.x - o.x);
}

/** Per-vertex reflex flags: true where the interior angle exceeds 180 degrees, robust to either winding. */
function reflexFlags(poly: LocalXZ[]): boolean[] {
  const n = poly.length;
  const sign = Math.sign(signedArea(poly)) || 1;
  const flags: boolean[] = [];
  for (let i = 0; i < n; i++) {
    const prev = poly[(i - 1 + n) % n];
    const cur = poly[i];
    const next = poly[(i + 1) % n];
    flags.push(cross(prev, cur, next) * sign < -EPS);
  }
  return flags;
}

interface RayHit {
  /** index of the edge (k -> k+1) the ray first meets */
  k: number;
  /** the meeting point */
  p: LocalXZ;
  /** ray parameter (distance, since dir is unit length) */
  t: number;
}

/** First boundary point a unit-direction ray from vertex `vi` meets, skipping that vertex's own two incident edges. */
function rayFirstHit(poly: LocalXZ[], vi: number, dir: { x: number; z: number }): RayHit | null {
  const origin = poly[vi];
  const n = poly.length;
  let bestT = Infinity;
  let bestK = -1;
  let bestP: LocalXZ | null = null;
  for (let k = 0; k < n; k++) {
    if (k === vi || (k + 1) % n === vi) continue; // skip the incident edges
    const a = poly[k];
    const b = poly[(k + 1) % n];
    const ex = b.x - a.x;
    const ez = b.z - a.z;
    const den = dir.x * -ez - dir.z * -ex;
    if (Math.abs(den) < 1e-12) continue; // parallel
    const rx = a.x - origin.x;
    const rz = a.z - origin.z;
    const t = (rx * -ez - rz * -ex) / den;
    const u = (dir.x * rz - dir.z * rx) / den;
    if (t > 1e-6 && u >= -1e-9 && u <= 1 + 1e-9 && t < bestT) {
      bestT = t;
      bestK = k;
      bestP = { x: origin.x + t * dir.x, z: origin.z + t * dir.z };
    }
  }
  return bestK < 0 || bestP === null ? null : { k: bestK, p: bestP, t: bestT };
}

/** Split a simple polygon along the chord from vertex `i` to point `P` on edge (k -> k+1). */
function splitPolygon(poly: LocalXZ[], i: number, k: number, P: LocalXZ): [LocalXZ[], LocalXZ[]] {
  const n = poly.length;
  const a: LocalXZ[] = [poly[i]];
  let idx = i;
  while (idx !== k) {
    idx = (idx + 1) % n;
    a.push(poly[idx]);
  }
  a.push(P);
  const b: LocalXZ[] = [P];
  idx = (k + 1) % n;
  b.push(poly[idx]);
  while (idx !== i) {
    idx = (idx + 1) % n;
    b.push(poly[idx]);
  }
  return [a, b];
}

interface Candidate {
  i: number;
  k: number;
  P: LocalXZ;
  cutLen: number;
  di: number;
}

/** All reflex-vertex edge-extension cuts of a piece. */
function reflexCandidates(poly: LocalXZ[]): Candidate[] {
  const n = poly.length;
  const flags = reflexFlags(poly);
  const out: Candidate[] = [];
  for (let i = 0; i < n; i++) {
    if (!flags[i]) continue;
    const a = poly[(i - 1 + n) % n];
    const r = poly[i];
    const b = poly[(i + 1) % n];
    // Continue each incident edge past the reflex vertex — both rays point into
    // the interior, and lie along a real wall direction.
    const dirs = [
      { x: r.x - a.x, z: r.z - a.z },
      { x: r.x - b.x, z: r.z - b.z },
    ];
    for (let di = 0; di < dirs.length; di++) {
      const len = Math.hypot(dirs[di].x, dirs[di].z);
      if (len < 1e-9) continue;
      const dir = { x: dirs[di].x / len, z: dirs[di].z / len };
      const hit = rayFirstHit(poly, i, dir);
      if (!hit || hit.k === i || (hit.k + 1) % n === i) continue;
      out.push({ i, k: hit.k, P: hit.p, cutLen: hit.t, di });
    }
  }
  return out;
}

/** Sutherland–Hodgman clip of a polygon against the half-plane where the (origin, normal) signed distance is <= 0. */
function clipHalfPlane(poly: LocalXZ[], ox: number, oz: number, nx: number, nz: number): LocalXZ[] {
  const out: LocalXZ[] = [];
  const n = poly.length;
  const side = (p: LocalXZ) => nx * (p.x - ox) + nz * (p.z - oz);
  for (let i = 0; i < n; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % n];
    const sa = side(a);
    const sb = side(b);
    if (sa <= EPS) out.push(a);
    if ((sa < -EPS && sb > EPS) || (sa > EPS && sb < -EPS)) {
      const t = sa / (sa - sb);
      out.push({ x: a.x + t * (b.x - a.x), z: a.z + t * (b.z - a.z) });
    }
  }
  // Drop coincident consecutive points the clip may introduce.
  const dedup: LocalXZ[] = [];
  for (let i = 0; i < out.length; i++) {
    const p = out[i];
    const q = out[(i + 1) % out.length];
    if (Math.hypot(p.x - q.x, p.z - q.z) > 1e-7) dedup.push(p);
  }
  return dedup;
}

/** Guillotine fallback for a convex/stuck piece: halve it perpendicular to its long axis through the centre. */
function guillotine(poly: LocalXZ[]): LocalXZ[][] {
  const box = orientedBbox(poly);
  const a = clipHalfPlane(poly, box.centre.x, box.centre.z, box.dir.x, box.dir.z);
  const b = clipHalfPlane(poly, box.centre.x, box.centre.z, -box.dir.x, -box.dir.z);
  return [a, b].filter((p) => p.length >= 3);
}

/** True when candidate `c` beats the incumbent best (lexicographic: smaller worst-child overhang, then shorter cut, then lower indices). */
function isBetterCut(
  candidateWorst: number,
  candidateLen: number,
  candidateI: number,
  candidateDi: number,
  bestWorst: number,
  bestLen: number,
  bestI: number,
  bestDi: number,
): boolean {
  if (candidateWorst < bestWorst - EPS) return true;
  if (candidateWorst > bestWorst + EPS) return false;
  if (candidateLen < bestLen - EPS) return true;
  if (candidateLen > bestLen + EPS) return false;
  if (candidateI !== bestI) return candidateI < bestI;
  return candidateDi < bestDi;
}

function decomposeChecked(original: LocalXZ[]): LocalXZ[][] {
  const result: LocalXZ[][] = [];

  const recurse = (poly: LocalXZ[], depth: number): void => {
    if (result.length > MAX_LOBES) {
      result.push(poly);
      return;
    }
    if (lobeOverhang(poly, original) <= ROOF_LOBE_MARGIN_M || depth >= MAX_LOBE_DEPTH) {
      result.push(poly);
      return;
    }

    let best: { a: LocalXZ[]; b: LocalXZ[] } | null = null;
    let bestWorst = Infinity;
    let bestLen = Infinity;
    let bestI = Infinity;
    let bestDi = Infinity;
    for (const c of reflexCandidates(poly)) {
      const [a, b] = splitPolygon(poly, c.i, c.k, c.P);
      if (a.length < 3 || b.length < 3) continue;
      const worst = Math.max(lobeOverhang(a, original), lobeOverhang(b, original));
      if (isBetterCut(worst, c.cutLen, c.i, c.di, bestWorst, bestLen, bestI, bestDi)) {
        bestWorst = worst;
        bestLen = c.cutLen;
        bestI = c.i;
        bestDi = c.di;
        best = { a, b };
      }
    }

    if (!best) {
      // No usable reflex cut (convex or numerically stuck) — halve it.
      const parts = guillotine(poly);
      if (parts.length >= 2) {
        for (const part of parts) recurse(part, depth + 1);
        return;
      }
      result.push(poly);
      return;
    }
    recurse(best.a, depth + 1);
    recurse(best.b, depth + 1);
  };

  recurse(original, 0);
  return result;
}

/**
 * Decomposes a footprint ring (open — first point != last, as stored in
 * buildings.json) into one or more roof lobes. Near-rectangular footprints
 * return the input ring unchanged as a single lobe (so buildRoof stays
 * byte-identical to the pre-#114 output); concave footprints return >=2
 * rectangular-ish sub-polygons, each within ROOF_LOBE_MARGIN_M of the original
 * polygon.
 *
 * Never throws and never returns an empty list (hard constraint #7: a
 * decomposition failure falls back to the whole ring — the current single-bbox
 * roof — never a crash and never a roofless building). Rings with fewer than 3
 * points are returned unchanged for the caller's existing degenerate guard.
 */
export function decomposeFootprint(ring: LocalXZ[]): LocalXZ[][] {
  if (ring.length < 3) return [ring];
  for (const p of ring) {
    if (!Number.isFinite(p.x) || !Number.isFinite(p.z)) return [ring];
  }
  let lobes: LocalXZ[][];
  try {
    lobes = decomposeChecked(ring);
  } catch {
    return [ring];
  }
  // Keep only valid simple sub-polygons; a stray degenerate sliver is dropped
  // (it has no area to roof), and if nothing survives we fall back to the ring.
  const valid = lobes.filter(
    (l) => l.length >= 3 && l.every((p) => Number.isFinite(p.x) && Number.isFinite(p.z)) && polygonArea(l) > 1e-6,
  );
  if (valid.length === 0) return [ring];
  // When the whole ring is accepted as one lobe, decomposeChecked pushed the
  // exact input array, so `valid` is `[ring]` and the caller's ridgeAxis sees
  // identical vertices — the single-lobe path reproduces today's bytes.
  return valid;
}
